/**
 * Module Registry
 *
 * Central registry for all module specifications in botbat-console.
 * Provides registration, lookup, and navigation generation utilities.
 */

import { matchPath } from "react-router-dom";
import type {
  ModuleSpec,
  NavGroup,
  NavSubgroup,
  NavItem,
  NavGroupId,
  UserContext,
  FeatureFlagContext,
  BreadcrumbTrail,
  Breadcrumb,
  NAV_GROUP_ORDER,
  NAV_GROUP_I18N_KEYS,
} from "./types";
import {
  NAV_GROUP_ORDER as GROUP_ORDER,
  NAV_GROUP_I18N_KEYS as GROUP_KEYS,
  CUSTOMER_SUBGROUPS,
} from "./types";
import type { TFunction } from "i18next";

// ============================================================================
// Module Registration Error
// ============================================================================

export class ModuleRegistrationError extends Error {
  constructor(
    public readonly moduleId: string,
    public readonly reason: string,
  ) {
    super(`Failed to register module '${moduleId}': ${reason}`);
    this.name = "ModuleRegistrationError";
  }
}

// ============================================================================
// Module Registry Implementation
// ============================================================================

class ModuleRegistryImpl {
  private modules = new Map<string, ModuleSpec>();
  private pathToId = new Map<string, string>();

  /**
   * Register a module specification.
   * @throws ModuleRegistrationError if validation fails
   */
  register(spec: ModuleSpec): void {
    // Validate module ID
    if (!spec.id) {
      throw new ModuleRegistrationError(
        spec.id || "unknown",
        "Module ID is required",
      );
    }
    if (!/^[a-z][a-z0-9-]*$/.test(spec.id)) {
      throw new ModuleRegistrationError(
        spec.id,
        "Module ID must be kebab-case",
      );
    }
    if (spec.id.length > 50) {
      throw new ModuleRegistrationError(
        spec.id,
        "Module ID must be 50 characters or less",
      );
    }

    // Check for duplicate ID
    if (this.modules.has(spec.id)) {
      throw new ModuleRegistrationError(
        spec.id,
        `Module with ID '${spec.id}' already registered`,
      );
    }

    // Validate and register path
    if (spec.nav?.path) {
      if (!spec.nav.path.startsWith("/")) {
        throw new ModuleRegistrationError(
          spec.id,
          "Navigation path must start with /",
        );
      }

      const existingId = this.pathToId.get(spec.nav.path);
      if (existingId) {
        throw new ModuleRegistrationError(
          spec.id,
          `Path '${spec.nav.path}' already registered by module '${existingId}'`,
        );
      }

      this.pathToId.set(spec.nav.path, spec.id);
    }

    // Validate parent exists (if specified)
    if (spec.nav?.parent && !this.modules.has(spec.nav.parent)) {
      // Allow forward references - parent may be registered later
      // Validation will happen at runtime if needed
    }

    // Validate icon - Lucide icons are ForwardRefExoticComponent which are objects with $$typeof
    // They can be called as functions (React components) but typeof returns 'object'
    if (!spec.icon) {
      throw new ModuleRegistrationError(spec.id, "Icon is required");
    }
    // Check if it's a valid React component (function or forwardRef object)
    const isValidIcon =
      typeof spec.icon === "function" ||
      (typeof spec.icon === "object" &&
        spec.icon !== null &&
        "$$typeof" in spec.icon);
    if (!isValidIcon) {
      throw new ModuleRegistrationError(
        spec.id,
        "Icon must be a valid Lucide icon component",
      );
    }

    // Validate i18n
    if (!spec.i18n?.namespace) {
      throw new ModuleRegistrationError(spec.id, "i18n namespace is required");
    }

    // Validate meta
    if (
      !spec.meta?.status ||
      !["active", "in-development", "coming-soon"].includes(spec.meta.status)
    ) {
      throw new ModuleRegistrationError(
        spec.id,
        "Valid meta.status is required",
      );
    }

    this.modules.set(spec.id, spec);
  }

  /**
   * Register multiple module specifications.
   */
  registerAll(specs: ModuleSpec[]): void {
    for (const spec of specs) {
      this.register(spec);
    }
  }

  /**
   * Get a module by its ID.
   */
  getById(id: string): ModuleSpec | undefined {
    return this.modules.get(id);
  }

  /**
   * Get a module by the current route path.
   * Uses React Router's matchPath for pattern matching.
   */
  getByPath(pathname: string): ModuleSpec | undefined {
    // First try exact match
    const exactId = this.pathToId.get(pathname);
    if (exactId) {
      return this.modules.get(exactId);
    }

    // Try pattern matching for dynamic routes
    for (const [path, id] of this.pathToId.entries()) {
      const match = matchPath({ path, end: false }, pathname);
      if (match) {
        return this.modules.get(id);
      }
    }

    return undefined;
  }

  /**
   * Get all registered modules.
   */
  getAll(): ModuleSpec[] {
    return Array.from(this.modules.values());
  }

  /**
   * Get all registered module IDs.
   */
  getAllIds(): string[] {
    return Array.from(this.modules.keys());
  }

  /**
   * Check if a module exists.
   */
  has(id: string): boolean {
    return this.modules.has(id);
  }

  /**
   * Get count of registered modules.
   */
  size(): number {
    return this.modules.size;
  }

  /**
   * Clear all registered modules (for testing).
   */
  clear(): void {
    this.modules.clear();
    this.pathToId.clear();
  }

  /**
   * Check if a module is visible based on permissions and feature flags.
   */
  private isModuleVisible(
    spec: ModuleSpec,
    userContext: UserContext,
    flagContext?: FeatureFlagContext,
  ): boolean {
    // Check feature flag
    if (spec.meta.featureFlag) {
      if (!flagContext?.isEnabled(spec.meta.featureFlag)) {
        return false;
      }
    }

    // Check permissions
    if (spec.permission) {
      // If permissions are loading, show the item (optimistic)
      if (userContext.isLoading) {
        return true;
      }

      if (
        !userContext.ability.can(
          spec.permission.action,
          spec.permission.subject,
        )
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Build a NavItem from a ModuleSpec.
   */
  private buildNavItem(
    spec: ModuleSpec,
    t: TFunction,
    getNotificationState?: (id: string) => boolean,
  ): NavItem {
    const namespace = spec.i18n.namespace;
    const titleKey = spec.i18n.titleKey || "title";
    const descriptionKey = spec.i18n.descriptionKey;
    const navLabelKey = spec.i18n.navLabelKey || titleKey;

    // Try to get label from modules namespace first, then fall back to module's own namespace
    let label = t(`modules:${spec.id}.title`, { defaultValue: "" });
    if (!label) {
      label = t(`${namespace}:${navLabelKey}`, { defaultValue: spec.id });
    }

    let description = spec.meta.description;
    if (descriptionKey) {
      const translatedDesc = t(`modules:${spec.id}.description`, {
        defaultValue: "",
      });
      if (translatedDesc) {
        description = translatedDesc;
      } else {
        const moduleDesc = t(`${namespace}:${descriptionKey}`, {
          defaultValue: "",
        });
        if (moduleDesc) {
          description = moduleDesc;
        }
      }
    }

    return {
      id: spec.id,
      to: spec.nav?.path || `/${spec.id}`,
      icon: spec.icon,
      label,
      status: spec.meta.status,
      description,
      disabled: spec.meta.status === "coming-soon",
      hasNotification: getNotificationState?.(spec.id) || false,
    };
  }

  /**
   * Get navigation groups filtered by user context and feature flags.
   */
  getNavGroups(
    userContext: UserContext,
    t: TFunction,
    flagContext?: FeatureFlagContext,
    getNotificationState?: (id: string) => boolean,
  ): NavGroup[] {
    const groupMap = new Map<NavGroupId, NavItem[]>();

    // Initialize empty arrays for all groups
    for (const groupId of GROUP_ORDER) {
      groupMap.set(groupId, []);
    }

    // Collect visible modules into their groups
    for (const spec of this.modules.values()) {
      if (!spec.nav) continue;

      if (!this.isModuleVisible(spec, userContext, flagContext)) {
        continue;
      }

      const navItem = this.buildNavItem(spec, t, getNotificationState);
      const items = groupMap.get(spec.nav.group);
      if (items) {
        items.push(navItem);
      }
    }

    // Sort items within each group by order
    for (const [groupId, items] of groupMap.entries()) {
      items.sort((a, b) => {
        const specA = this.modules.get(a.id);
        const specB = this.modules.get(b.id);
        const orderA = specA?.nav?.order ?? 100;
        const orderB = specB?.nav?.order ?? 100;
        return orderA - orderB;
      });
    }

    // Build final groups array (only include non-empty groups)
    const groups: NavGroup[] = [];
    for (const groupId of GROUP_ORDER) {
      const allItems = groupMap.get(groupId);
      if (!allItems || allItems.length === 0) continue;

      const titleKey = GROUP_KEYS[groupId];

      // Check if any items have subgroups defined
      const hasSubgroups = allItems.some(item => {
        const spec = this.modules.get(item.id);
        return spec?.nav?.subgroup;
      });

      if (hasSubgroups) {
        // Partition into top-level items and subgrouped items
        const topLevelItems: NavItem[] = [];
        const subgroupItemMap = new Map<string, NavItem[]>();

        for (const item of allItems) {
          const spec = this.modules.get(item.id);
          const subgroupId = spec?.nav?.subgroup;
          if (subgroupId) {
            if (!subgroupItemMap.has(subgroupId)) {
              subgroupItemMap.set(subgroupId, []);
            }
            subgroupItemMap.get(subgroupId)!.push(item);
          } else {
            topLevelItems.push(item);
          }
        }

        // Build subgroups from the CUSTOMER_SUBGROUPS definition order
        const subgroups: NavSubgroup[] = [];
        for (const subgroupDef of CUSTOMER_SUBGROUPS) {
          const sgItems = subgroupItemMap.get(subgroupDef.id);
          if (sgItems && sgItems.length > 0) {
            subgroups.push({
              id: subgroupDef.id,
              title: t(subgroupDef.i18nKey, { defaultValue: subgroupDef.id }),
              items: sgItems,
            });
          }
        }

        groups.push({
          id: groupId,
          title: t(titleKey, { defaultValue: groupId }),
          items: topLevelItems,
          subgroups: subgroups.length > 0 ? subgroups : undefined,
        });
      } else {
        groups.push({
          id: groupId,
          title: t(titleKey, { defaultValue: groupId }),
          items: allItems,
        });
      }
    }

    return groups;
  }

  /**
   * Generate breadcrumb trail for a given path.
   */
  getBreadcrumbs(pathname: string, t: TFunction): BreadcrumbTrail {
    const breadcrumbs: BreadcrumbTrail = [];

    // Always start with Home
    breadcrumbs.push({
      label: t("modules:breadcrumbs.home", { defaultValue: "Home" }),
      path: "/",
    });

    // Don't add more breadcrumbs for home page
    if (pathname === "/" || pathname === "") {
      return breadcrumbs;
    }

    // Get the current module
    const currentModule = this.getByPath(pathname);

    if (currentModule) {
      // If module has a parent, add parent first
      if (currentModule.nav?.parent) {
        const parentModule = this.getById(currentModule.nav.parent);
        if (parentModule && parentModule.nav) {
          const parentLabel = t(`modules:${parentModule.id}.title`, {
            defaultValue: parentModule.id,
          });
          breadcrumbs.push({
            label: parentLabel,
            path: parentModule.nav.path,
            moduleId: parentModule.id,
          });
        }
      }

      // Add current module (no path since it's current)
      const currentLabel = t(`modules:${currentModule.id}.title`, {
        defaultValue: currentModule.id,
      });
      breadcrumbs.push({
        label: currentLabel,
        moduleId: currentModule.id,
        // No path for current page
      });
    } else {
      // No module found - try to build from path segments
      const segments = pathname.split("/").filter(Boolean);
      let currentPath = "";

      for (let i = 0; i < segments.length; i++) {
        currentPath += `/${segments[i]}`;
        const isLast = i === segments.length - 1;
        const moduleAtPath = this.getByPath(currentPath);

        if (moduleAtPath) {
          const label = t(`modules:${moduleAtPath.id}.title`, {
            defaultValue: segments[i],
          });
          breadcrumbs.push({
            label,
            path: isLast ? undefined : currentPath,
            moduleId: moduleAtPath.id,
          });
        } else {
          // Capitalize segment as fallback label
          const label =
            segments[i].charAt(0).toUpperCase() + segments[i].slice(1);
          breadcrumbs.push({
            label,
            path: isLast ? undefined : currentPath,
          });
        }
      }
    }

    return breadcrumbs;
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

/**
 * Global module registry instance.
 */
export const moduleRegistry = new ModuleRegistryImpl();

/**
 * Register a module specification.
 */
export function registerModule(spec: ModuleSpec): void {
  moduleRegistry.register(spec);
}

/**
 * Register multiple module specifications.
 */
export function registerModules(specs: ModuleSpec[]): void {
  moduleRegistry.registerAll(specs);
}

/**
 * Get a module by ID.
 */
export function getModuleById(id: string): ModuleSpec | undefined {
  return moduleRegistry.getById(id);
}

/**
 * Get a module by path.
 */
export function getModuleByPath(pathname: string): ModuleSpec | undefined {
  return moduleRegistry.getByPath(pathname);
}

/**
 * Get all registered modules.
 */
export function getAllModules(): ModuleSpec[] {
  return moduleRegistry.getAll();
}

/**
 * Get navigation groups.
 */
export function getNavGroups(
  userContext: UserContext,
  t: TFunction,
  flagContext?: FeatureFlagContext,
  getNotificationState?: (id: string) => boolean,
): NavGroup[] {
  return moduleRegistry.getNavGroups(
    userContext,
    t,
    flagContext,
    getNotificationState,
  );
}

/**
 * Get breadcrumbs for a path.
 */
export function getBreadcrumbs(
  pathname: string,
  t: TFunction,
): BreadcrumbTrail {
  return moduleRegistry.getBreadcrumbs(pathname, t);
}
